import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, X, Edit2, LogOut } from "lucide-react";
import api, { formatINR, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import { PAYMENT_LABEL, STATUS_LABEL, STATUS_STYLE, errorMessage, fmtDay } from "../lib/stays";

export default function DashboardPage() {
  const { user, logout, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [editMode, setEditMode] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookings/me");
      setBookings(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const cancel = async (b) => {
    const p = b.cancellation_preview;
    const msg = b.refund_type === "NON_REFUNDABLE"
      ? "This booking is non-refundable. Cancelling will NOT give you a refund. Cancel anyway?"
      : `Cancel this booking? Refund per policy: ${p ? p.refund_percent : 0}%.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const saveProfile = async () => {
    try {
      await api.put("/auth/profile", profile);
      await refresh();
      toast.success("Profile updated");
      setEditMode(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="pt-32 pb-24 grain min-h-screen" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <div className="eyebrow">My Account</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-4">Welcome, {user?.name?.split(" ")[0]}</h1>
          </div>
          <button onClick={logout} className="btn-ghost flex items-center gap-2" data-testid="dashboard-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {/* Profile */}
        <div className="glass p-8 mb-12" data-testid="profile-section">
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">Profile</div>
            <button onClick={() => setEditMode(!editMode)} className="text-gold hover:text-gold-light" data-testid="edit-profile-toggle">
              <Edit2 size={14} />
            </button>
          </div>
          {editMode ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-white/60 uppercase">Name</Label>
                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" data-testid="edit-name-input" />
              </div>
              <div>
                <Label className="text-xs text-white/60 uppercase">Phone</Label>
                <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-2 bg-[#0f0f0f] border-white/10 h-12" data-testid="edit-phone-input" />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button onClick={saveProfile} className="btn-gold" data-testid="save-profile-button">Save</button>
                <button onClick={() => setEditMode(false)} className="btn-ghost">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider">Name</div>
                <div className="text-white mt-2">{user?.name}</div>
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider">Email</div>
                <div className="text-white mt-2">{user?.email}</div>
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider">Phone</div>
                <div className="text-white mt-2">{user?.phone || "—"}</div>
              </div>
            </div>
          )}
        </div>

        {/* Bookings */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow">My Bookings</div>
            <h2 className="font-display text-3xl mt-2">Reservations</h2>
          </div>
          <Link to="/booking" className="btn-gold" data-testid="dashboard-new-booking">New Booking</Link>
        </div>

        {loading ? (
          <div className="text-center text-white/40 py-12">Loading...</div>
        ) : bookings.length === 0 ? (
          <div className="glass p-12 text-center" data-testid="no-bookings-state">
            <Calendar className="text-gold mx-auto mb-4" size={32} />
            <div className="font-display text-2xl">No bookings yet</div>
            <p className="text-white/50 mt-2 mb-6">Reserve your first stay with us.</p>
            <Link to="/booking" className="btn-gold">Book Now</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} className="glass p-6 flex flex-col lg:flex-row items-start lg:items-center gap-6" data-testid={`booking-card-${b.id}`}>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`text-xs uppercase tracking-[0.2em] border px-2 py-1 ${STATUS_STYLE[b.status] || ""}`}>
                      {STATUS_LABEL[b.status] || b.status}
                    </div>
                    <div className="text-xs text-white/40">{b.reference || `#${String(b.id).padStart(6, "0")}`}</div>
                    {b.refund_type === "NON_REFUNDABLE" && <div className="text-xs text-red-300 border border-red-500/30 px-2 py-1">Non-refundable</div>}
                  </div>
                  <div className="font-display text-xl mt-3">{b.property?.name || "Stay"} · <span className="capitalize">{b.booking_type}</span></div>
                  <div className="text-sm text-white/60 mt-1">
                    {fmtDay(b.check_in_date, { day: "numeric", month: "short", year: "numeric" })} ({b.check_in_time_label})
                    {" → "}
                    {fmtDay(b.check_out_date, { day: "numeric", month: "short", year: "numeric" })} ({b.check_out_time_label})
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl text-gold">{formatINR(b.total_amount)}</div>
                  <div className="text-xs text-white/40 mt-1">{PAYMENT_LABEL[b.payment_status] || b.payment_status}</div>
                </div>
                <div className="flex flex-col items-start lg:items-end gap-2">
                  <Link to={`/booking/confirmation/${b.id}`} className="text-gold text-sm hover:underline" data-testid={`view-booking-${b.id}`}>View details</Link>
                  {(b.status === "pending" || b.status === "confirmed") && (
                    <button onClick={() => cancel(b)} className="text-red-400 hover:text-red-300 flex items-center gap-2 text-sm" data-testid={`cancel-booking-${b.id}`}>
                      <X size={14} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
